import { PATTERN_TYPE_OPTIONS } from './patternTypes';
import { UPLOAD_TIMEFRAME_OPTIONS } from './patternFilters';

export const API_ENDPOINT_CATALOG = [
  {
    id: 'brands-list',
    group: 'brands',
    label: 'List brands',
    method: 'GET',
    path: '/brands/',
    description: 'List brands accessible with this API key.',
    fields: [],
  },
  {
    id: 'brands-detail',
    group: 'brands',
    label: 'Get brand',
    method: 'GET',
    path: '/brands/{brand_hash}/',
    description: 'Retrieve a single brand by hash.',
    fields: [{ key: 'brand_hash', label: 'Brand hash', required: true, source: 'brand' }],
  },
  {
    id: 'ideas-list',
    group: 'ideas',
    label: 'List ideas',
    method: 'GET',
    path: '/ideas/',
    description: 'List ideas for a brand. Requires brand_hash.',
    fields: [{ key: 'brand_hash', label: 'Brand hash', required: true, source: 'brand', query: true }],
  },
  {
    id: 'ideas-create',
    group: 'ideas',
    label: 'Generate idea',
    method: 'POST',
    path: '/ideas/',
    description: 'Generate a new idea for a brand using the same queue logic as the app (N=1).',
    fields: [
      { key: 'brand_hash', label: 'Brand hash', required: true, source: 'brand', query: true },
      { key: 'promotion_rating', label: 'Promotion rating (0-10)', query: true, type: 'number' },
    ],
  },
  {
    id: 'scripts-list',
    group: 'scripts',
    label: 'List scripts',
    method: 'GET',
    path: '/scripts/',
    description: 'List scripts for a brand. Requires brand_hash.',
    fields: [{ key: 'brand_hash', label: 'Brand hash', required: true, source: 'brand', query: true }],
  },
  {
    id: 'scripts-create',
    group: 'scripts',
    label: 'Generate script',
    method: 'POST',
    path: '/scripts/',
    description: 'Generate a script from an idea_hash, or from brand_hash (creates one idea first).',
    fields: [
      { key: 'idea_hash', label: 'Idea hash', query: true },
      { key: 'brand_hash', label: 'Brand hash', source: 'brand', query: true },
      { key: 'promotion_rating', label: 'Promotion rating (0-10)', query: true, type: 'number' },
    ],
  },
  {
    id: 'brands-audiences',
    group: 'brands',
    label: 'List brand audiences',
    method: 'GET',
    path: '/brands/{brand_hash}/audiences/',
    description: 'List audiences with a fit score for this brand. High-fit only by default.',
    fields: [
      { key: 'brand_hash', label: 'Brand hash', required: true, source: 'brand' },
      { key: 'include_low_fit', label: 'Include low fit', query: true, type: 'boolean' },
    ],
  },
  {
    id: 'audiences-detail',
    group: 'audiences',
    label: 'Get audience',
    method: 'GET',
    path: '/audiences/{audience_hash}/',
    description: 'Retrieve a single audience by hash.',
    fields: [{ key: 'audience_hash', label: 'Audience hash', required: true, source: 'audience' }],
  },
  {
    id: 'audiences-list',
    group: 'audiences',
    label: 'List audiences',
    method: 'GET',
    path: '/audiences/',
    description: 'List audiences accessible with this API key.',
    fields: [],
  },
  {
    id: 'audiences-patterns',
    group: 'audiences',
    label: 'Audience patterns',
    method: 'GET',
    path: '/audiences/{audience_hash}/patterns/',
    description: 'Return patterns for an audience (cached when available, generated on miss). Requires pattern_type.',
    fields: [
      { key: 'audience_hash', label: 'Audience hash', required: true, source: 'audience' },
      {
        key: 'pattern_type',
        label: 'Pattern type',
        required: true,
        query: true,
        type: 'select',
        options: PATTERN_TYPE_OPTIONS,
      },
      {
        key: 'upload_timeframe',
        label: 'Uploaded',
        query: true,
        type: 'select',
        options: UPLOAD_TIMEFRAME_OPTIONS,
        default: 'week',
      },
      {
        key: 'sort',
        label: 'Sort',
        query: true,
        type: 'select',
        default: 'video_count',
        options: [
          { value: 'video_count', label: 'Video count' },
          { value: 'views', label: 'Views' },
          { value: 'likes', label: 'Likes' },
          { value: 'comments', label: 'Comments' },
          { value: 'views_outlier', label: 'Views outlier' },
          { value: 'likes_outlier', label: 'Likes outlier' },
          { value: 'comments_outlier', label: 'Comments outlier' },
        ],
      },
      {
        key: 'more_than_one_video',
        label: '>1 video',
        query: true,
        type: 'boolean',
        default: true,
      },
      {
        key: 'show_other_options',
        label: "Include 'other' options",
        query: true,
        type: 'boolean',
        default: false,
      },
    ],
  },
  {
    id: 'audiences-highlights',
    group: 'audiences',
    label: 'Audience brand highlights',
    method: 'GET',
    path: '/audiences/{audience_hash}/highlights/',
    description: 'Return highlights for a brand within an audience.',
    fields: [
      { key: 'audience_hash', label: 'Audience hash', required: true, source: 'audience' },
      { key: 'brand_hash', label: 'Brand hash', required: true, query: true },
    ],
  },
];

const BRAND_PLAYGROUND_IDS = [
  'brands-list',
  'brands-detail',
  'ideas-list',
  'ideas-create',
  'scripts-list',
  'scripts-create',
  'brands-audiences',
  'audiences-highlights',
];

const AUDIENCE_PLAYGROUND_IDS = [
  'audiences-detail',
  'audiences-list',
  'brands-audiences',
  'audiences-patterns',
  'audiences-highlights',
];

export function endpointsForContext(context = 'brand') {
  const byId = Object.fromEntries(API_ENDPOINT_CATALOG.map((e) => [e.id, e]));
  if (context === 'audience') {
    // Audience page nests under a brand: show audience endpoints plus brand ones (no dupes).
    const brandOnlyIds = BRAND_PLAYGROUND_IDS.filter((id) => !AUDIENCE_PLAYGROUND_IDS.includes(id));
    return [
      ...AUDIENCE_PLAYGROUND_IDS.map((id) => ({ ...byId[id], section: 'Audience APIs' })),
      ...brandOnlyIds.map((id) => ({ ...byId[id], section: 'Brand APIs' })),
    ].filter((e) => e.id);
  }
  return BRAND_PLAYGROUND_IDS.map((id) => byId[id]).filter(Boolean);
}

export function pickPreferredToken(tokens, { brandHash, projectHash } = {}) {
  const active = (tokens || []).filter((t) => t.is_active);
  if (!active.length) return null;
  if (brandHash) {
    const brandToken = active.find((t) => t.scope === 'brand' && t.brand_hash === brandHash);
    if (brandToken) return brandToken;
  }
  if (projectHash) {
    const projectToken = active.find((t) => t.scope === 'project' && t.project_hash === projectHash);
    if (projectToken) return projectToken;
  }
  const accountToken = active.find((t) => t.scope === 'account');
  return accountToken || active[0];
}

/** Empty param display token, e.g. audience_hash → AUDIENCEHASH */
export function placeholderForParam(key) {
  return String(key || '').replace(/_/g, '').toUpperCase();
}

export function resolveEndpointPath(endpoint, values = {}, { usePlaceholders = false } = {}) {
  return endpoint.path.replace(/\{([^}]+)\}/g, (_, key) => {
    const raw = values[key];
    if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
      return encodeURIComponent(String(raw).trim());
    }
    return usePlaceholders ? placeholderForParam(key) : '';
  });
}

export function missingRequiredFields(endpoint, values = {}) {
  if (!endpoint) return [];
  return (endpoint.fields || []).filter((field) => {
    if (!field.required) return false;
    const raw = values[field.key];
    return raw === undefined || raw === null || String(raw).trim() === '';
  });
}
